import type { YuvFormat } from "../compute/yuvdefs";
import type { FormatSource } from "../compute/yuv_format";
import type { CameraFacing } from "./camerainfo";

export type CaptureFrameRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CaptureFrameColorSpace = {
  primaries: string | null;
  transfer: string | null;
  matrix: string | null;
  fullRange: boolean | null;
};

export type CaptureFramePlane = {
  index: number;
  offset: number;
  stride: number;
  width: number;
  height: number;
  byteLength: number | null;
};

export type CaptureTexturePlane = {
  label: string;
  width: number;
  height: number;
};

export type CaptureFrameDiagnostic = {
  sourceSequence: number;
  path: "main" | "worker";
  selectedCameraFriendly: string;
  selectedCameraLabel: string;
  selectedCameraSuffix: string;
  selectedCameraFacing: CameraFacing;
  trackLabel: string | null;
  trackWidth: number | null;
  trackHeight: number | null;
  trackFrameRate: number | null;
  trackFacingMode: string | null;
  trackResizeMode: string | null;
  trackAspectRatio: number | null;
  userAgent: string | null;
  platform: string | null;
  uaDataPlatform: string | null;
  deviceModel: string | null;
  reportedFormat: string | null;
  resolvedFormat: YuvFormat;
  formatSource: FormatSource;
  formatWarning: string | null;
  allocationSize: number | null;
  codedWidth: number;
  codedHeight: number;
  displayWidth: number;
  displayHeight: number;
  visibleRect: CaptureFrameRect | null;
  colorSpace: CaptureFrameColorSpace | null;
  screenOrientationType: string | null;
  screenOrientationAngle: number | null;
  layoutPlanes: CaptureFramePlane[];
  texturePlanes: CaptureTexturePlane[];
};

export type CaptureOverlayDiagnostic = {
  displayMode: number;
  screenOrientationType: string | null;
  mirrored: boolean;
  srcWidth: number;
  srcHeight: number;
  dstWidth: number;
  dstHeight: number;
};

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatNumber(value: number | null | undefined, digits = 2): string {
  if (!Number.isFinite(value ?? NaN)) return "?";
  const fixed = (value as number).toFixed(digits);
  return fixed
    .replace(/\.00$/, "")
    .replace(/(\.\d*[1-9])0+$/, "$1")
    .replace(/^-0(?:\.0+)?$/, "0");
}

function formatRect(rect: CaptureFrameRect | null): string {
  if (!rect) return "-";
  return `${formatNumber(rect.x)},${formatNumber(rect.y)}+${formatNumber(rect.width)}x${formatNumber(rect.height)}`;
}

function formatColorSpace(colorSpace: CaptureFrameColorSpace | null): string {
  if (!colorSpace) return "-";
  const fullRange =
    colorSpace.fullRange == null ? "?" : colorSpace.fullRange ? "1" : "0";
  return [
    colorSpace.primaries ?? "-",
    colorSpace.transfer ?? "-",
    colorSpace.matrix ?? "-",
    `full=${fullRange}`,
  ].join("/");
}

function quote(value: string | null | undefined): string {
  const text = value && value.length > 0 ? value : "(none)";
  return `"${text.replace(/"/g, '\\"')}"`;
}

function formatTrackSize(width: number | null, height: number | null): string {
  if ((width ?? 0) <= 0 || (height ?? 0) <= 0) return "?x?";
  return `${width}x${height}`;
}

function formatTrackRate(value: number | null): string {
  if (!Number.isFinite(value ?? NaN) || (value ?? 0) <= 0) return "?";
  return formatNumber(value, 1);
}

function formatPlane(plane: CaptureFramePlane): string {
  const bytes = plane.byteLength == null ? "?" : String(plane.byteLength);
  return `p${plane.index} off${plane.offset} s${plane.stride} ${plane.width}x${plane.height} b${bytes}`;
}

function formatTexturePlane(plane: CaptureTexturePlane): string {
  return `${plane.label} ${plane.width}x${plane.height}`;
}

export function extractCaptureFrameRect(value: unknown): CaptureFrameRect | null {
  if (!value || typeof value !== "object") return null;
  const rect = value as Record<string, unknown>;
  const x = numberOrNull(rect.x);
  const y = numberOrNull(rect.y);
  const width = numberOrNull(rect.width);
  const height = numberOrNull(rect.height);
  if (x == null || y == null || width == null || height == null) return null;
  return { x, y, width, height };
}

export function extractCaptureColorSpace(value: unknown): CaptureFrameColorSpace | null {
  if (!value || typeof value !== "object") return null;
  const color = value as Record<string, unknown>;
  const out: CaptureFrameColorSpace = {
    primaries: stringOrNull(color.primaries),
    transfer: stringOrNull(color.transfer),
    matrix: stringOrNull(color.matrix),
    fullRange: typeof color.fullRange === "boolean" ? color.fullRange : null,
  };
  return out.primaries || out.transfer || out.matrix || out.fullRange != null
    ? out
    : null;
}

export function buildCaptureFrameDiagnosticLog(
  snapshot: CaptureFrameDiagnostic,
  overlay?: CaptureOverlayDiagnostic | null
): string {
  const screenType =
    overlay?.screenOrientationType ??
    snapshot.screenOrientationType ??
    "unknown";
  const angle =
    snapshot.screenOrientationAngle == null
      ? "?"
      : formatNumber(snapshot.screenOrientationAngle, 0);
  const head = [
    `[scanctx] seq=${snapshot.sourceSequence}`,
    `path=${snapshot.path}/${snapshot.resolvedFormat}(${snapshot.formatSource})`,
    `reported=${snapshot.reportedFormat ?? "-"}`,
    `cam=${quote(snapshot.selectedCameraFriendly)}`,
    `id=${snapshot.selectedCameraSuffix || "unknown"}`,
    `facing=${snapshot.selectedCameraFacing}`,
    `screen=${screenType}@${angle}`,
  ];
  if (overlay) {
    head.push(
      `ov=mode${overlay.displayMode}`,
      `mir=${overlay.mirrored ? 1 : 0}`,
      `disp=${overlay.srcWidth}x${overlay.srcHeight}->${overlay.dstWidth}x${overlay.dstHeight}`
    );
  }

  const lines = [
    head.join(" "),
    [
      `ua=${snapshot.userAgent ?? "-"}`,
      `platform=${snapshot.platform ?? "-"}`,
      `uad=${snapshot.uaDataPlatform ?? "-"}`,
      `model=${snapshot.deviceModel ?? "-"}`,
    ].join(" "),
    [
      `label=${quote(snapshot.selectedCameraLabel)}`,
      `track=${quote(snapshot.trackLabel ?? snapshot.selectedCameraLabel)}`,
      `${formatTrackSize(snapshot.trackWidth, snapshot.trackHeight)}@${formatTrackRate(snapshot.trackFrameRate)}fps`,
      `facingMode=${snapshot.trackFacingMode ?? "-"}`,
      `resize=${snapshot.trackResizeMode ?? "-"}`,
      `aspect=${formatNumber(snapshot.trackAspectRatio, 4)}`,
    ].join(" "),
    [
      `frame=${snapshot.codedWidth}x${snapshot.codedHeight}->${snapshot.displayWidth}x${snapshot.displayHeight}`,
      `visible=${formatRect(snapshot.visibleRect)}`,
      `alloc=${snapshot.allocationSize ?? "?"}`,
      `color=${formatColorSpace(snapshot.colorSpace)}`,
    ].join(" "),
    `layout=[${snapshot.layoutPlanes.map(formatPlane).join(" | ")}]`,
    `tex=[${snapshot.texturePlanes.map(formatTexturePlane).join(" | ")}]`,
  ];

  if (snapshot.formatWarning) {
    lines.push(`warn=${snapshot.formatWarning}`);
  }

  return lines.join("\n");
}
